import { AuthResponse, PostgrestSingleResponse, User, UserResponse } from '@supabase/supabase-js';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, from, map } from 'rxjs';

import { RoleResponseModel } from '@user/models/role.model';
import { Router } from '@angular/router';
import { SupabaseClientService } from '@app/services/supabase.client.service';
import { ToastService } from '@app/services/toast.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private router = inject(Router);
    private toastService = inject(ToastService);
    private supabaseClient = inject(SupabaseClientService);

    private readonly _user = signal<User | null>(null);
    private readonly _loading = signal(true);
    private readonly _initialized = signal(false);
    private readonly _isUserAdmin = signal(false);

    readonly user = computed(() => this._user());
    readonly isLoggedIn = computed(() => {
        return !!this._user();
    });
    readonly isLoading = computed(() => this._loading());
    readonly isInitialized = computed(() => this._initialized());
    readonly isUserAdmin = computed(() => {
        return this._isUserAdmin();
    });

    constructor() {
        this._loading.set(true);

        this.supabaseClient.client.auth.getSession().then(({ data }) => {
            this._user.set(data.session?.user ?? null);
            this._loading.set(false);
        });
        this.supabaseClient.client.auth.onAuthStateChange((_event, session) => {
            this._loading.set(false);
            this._user.set(session?.user ?? null);

            if (_event === 'SIGNED_OUT') {
                this.router.navigate(['/sign-in']);
            }

            if (_event === 'INITIAL_SESSION') {
                this._initialized.set(true);
            }

            // TODO Check auth once again
            if (_event === 'SIGNED_IN') {
                // this.router.navigate(['/dashboard']);
                // this._initialized.set(true);
            }
        });
    }

    refreshSession(): Promise<AuthResponse> {
        return this.supabaseClient.client.auth.refreshSession();
    }

    updateUserSession(): void {
        this.supabaseClient.client.auth.getSession().then(({ data }) => {
            this._user.set(data.session?.user ?? null);
        });
    }

    login(email: string, password: string): Observable<string | null> {
        return from(this.supabaseClient.client.auth.signInWithPassword({ email, password })).pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(({ error }: any) => {
                if (error) {
                    this.toastService.error(error?.message || 'Something went wrong');
                    return error.message;
                }
                return null;
            }),
        );
    }

    logout(): Observable<string | null> {
        return from(this.supabaseClient.client.auth.signOut()).pipe(
            map(({ error }) => {
                this.toastService.error(error?.message || 'Something went wrong during logout');
                return error ? error.message : null;
            }),
        );
    }

    getUserId(): string | null {
        return this._user()?.id ?? null;
    }

    getUserEmail(): string | null {
        return this._user()?.email ?? null;
    }

    updateUserPassword(newPassword: string): Observable<{user: User;}> {
        return from(
            this.supabaseClient.client.auth.updateUser({
                password: newPassword,
            }),
        ).pipe(
            map(({ data, error }: UserResponse) => {
                if (error) {
                    this.toastService.error(
                        error.message || 'Something went wrong password update',
                    );
                    throw error;
                }
                this.toastService.success('Password updated!');
                return data;
            }),
        );
    }

    getUserRole(): Observable<RoleResponseModel[]> {
        return from(
            this.supabaseClient.client.from('roles').select('*').eq('auth_id', this.user()?.id),
        ).pipe(
            map(({ data, error }: PostgrestSingleResponse<RoleResponseModel[]>) => {
                if (error) {
                    throw error;
                }
                if (data.length > 0 && data[0].role === 'admin') {
                    this._isUserAdmin.set(true);
                }
                return data;
            }),
        );
    }
}
